import {ChangeDetectorRef, Component, Inject, OnInit} from "@angular/core";
import {
	UntypedFormBuilder,
	UntypedFormControl,
	UntypedFormGroup,
	ValidatorFn,
	Validators,
	ReactiveFormsModule,
} from "@angular/forms";
import {MAT_DIALOG_DATA, MatDialogRef, MatDialogContent, MatDialogClose} from "@angular/material/dialog";
import {SourceCodeStorage} from "../../../core/models/Project";
import {SourceCodeStorageLocation} from "../../../core/models/Enums/SourceCodeStorageLocation";
import {TranslatableComponent} from "../../../core/models/TranslatableComponent";
import {TranslateService, TranslatePipe} from "@ngx-translate/core";
import marketUnit from "../../../core/config/MarketUnit";
import {MatIcon} from "@angular/material/icon";
import {CdkScrollable} from "@angular/cdk/scrolling";
import {MatRadioGroup, MatRadioButton} from "@angular/material/radio";
import {MatFormField, MatLabel, MatHint} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatExpansionPanelTitle} from "@angular/material/expansion";
import {EscapeHotkeyDirective} from "../../hotkey/directives/escape-hotkey.directive";
import {SaveHotkeyDirective} from "../../hotkey/directives/save-hotkey.directive";

@Component({
	selector: "app-add-source-code-dialog",
	templateUrl: "./add-source-code-dialog.component.html",
	styleUrls: ["./add-source-code-dialog.component.scss"],
	imports: [
		MatIcon,
		CdkScrollable,
		MatDialogContent,
		ReactiveFormsModule,
		MatRadioGroup,
		MatRadioButton,
		MatFormField,
		MatLabel,
		MatInput,
		MatExpansionPanelTitle,
		MatHint,
		EscapeHotkeyDirective,
		SaveHotkeyDirective,
		MatDialogClose,
		TranslatePipe,
	],
})
export class AddSourceCodeDialogComponent extends TranslatableComponent implements OnInit {
	sourceCodeStorage: SourceCodeStorage;
	isChecked: unknown;
	isNewProject: boolean;
	sourceCodeStorageLocation = SourceCodeStorageLocation;
	responsibleBusinessUnit: string;

	sourceCodeForm = new UntypedFormGroup({
		sourceCodeStorageLocation: new UntypedFormControl("", {
			validators: Validators.required,
		}),
		businessUnitResponsibilityFields: new UntypedFormGroup({
			stackIntegratedStorage: new UntypedFormControl(""),
			msgHub: new UntypedFormControl(""),
			cloudSourceStorage: new UntypedFormControl(""),
			reasonForOtherCloudSource: new UntypedFormControl(""),
		}),
	});

	constructor(
		private dialogRef: MatDialogRef<AddSourceCodeDialogComponent>,
		private _fb: UntypedFormBuilder,
		@Inject(MAT_DIALOG_DATA)
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		public data: any,
		private t: TranslateService,
		private cd: ChangeDetectorRef,
	) {
		super(t, "ProjectDataComponent");
		this.dialogRef.addPanelClass("mep-dialog");
	}

	ngOnInit(): void {
		this.sourceCodeStorage = this.data.sourceCodeStorage;

		if (!this.data.project) {
			this.isNewProject = true;
		}
		this.cd.detectChanges();
		this.sourceCodeForm?.get("sourceCodeStorageLocation").setValue(this.data?.project.sourceCodeStorageLocation);
		this.sourceCodeForm
			?.get("businessUnitResponsibilityFields.stackIntegratedStorage")
			.setValue(this.data?.project.stackIntegratedStorage);
		this.sourceCodeForm?.get("businessUnitResponsibilityFields.msgHub").setValue(this.data?.project.msgHub);
		this.sourceCodeForm
			?.get("businessUnitResponsibilityFields.cloudSourceStorage")
			.setValue(this.data?.project.cloudSourceStorage);
		this.sourceCodeForm
			?.get("businessUnitResponsibilityFields.reasonForOtherCloudSource")
			.setValue(this.data?.project.reasonForOtherCloudSource);
		this.onRadioButtonChanged();

		const buKey = this.data?.project?.businessUnit;
		this.responsibleBusinessUnit = buKey
			? (marketUnit.MarketUnit[buKey as keyof typeof marketUnit.MarketUnit] ?? buKey)
			: "N/A";
	}

	atLeastOneOptionSelected(minRequired): ValidatorFn {
		return function validate(formGroup: UntypedFormGroup) {
			let checked = 0;

			Object.keys(formGroup.controls).forEach((key) => {
				const control = formGroup.controls[key];

				if (control.value) {
					checked++;
				}
			});
			if (checked < minRequired) {
				return {noOptionsSelected: true};
			}

			return null;
		};
	}

	/**
	 * Closes the dialog
	 */
	selected: unknown;

	closeDialog() {
		this.dialogRef.close();
	}

	getResult() {
		return {
			sourceCodeStorageLocation: this.sourceCodeForm?.get("sourceCodeStorageLocation").value,
			stackIntegratedStorage: this.sourceCodeForm?.get("businessUnitResponsibilityFields").value.stackIntegratedStorage,
			msgHub: this.sourceCodeForm?.get("businessUnitResponsibilityFields").value.msgHub,
			cloudSourceStorage: this.sourceCodeForm?.get("businessUnitResponsibilityFields").value.cloudSourceStorage,
			reasonForOtherCloudSource: this.sourceCodeForm?.get("businessUnitResponsibilityFields").value
				.reasonForOtherCloudSource,
			sourceCodeForm: this.sourceCodeForm.dirty,
		};
	}

	onRadioButtonChanged() {
		let sourcecodeLocation = this.sourceCodeForm?.get("sourceCodeStorageLocation").value;
		if (sourcecodeLocation === "NO_SOURCE_CODE" || sourcecodeLocation === "CLIENT_RESPONSIBILITY") {
			this.sourceCodeForm.get("businessUnitResponsibilityFields").disable();
			this.sourceCodeForm.get("businessUnitResponsibilityFields").reset();
		}
		if (sourcecodeLocation === "BUSINESSUNIT_RESPONSIBILITY") {
			this.sourceCodeForm?.get("businessUnitResponsibilityFields").setValidators(this.atLeastOneOptionSelected(1));
			this.sourceCodeForm.get("businessUnitResponsibilityFields").enable();
		} else {
			this.sourceCodeForm.get("businessUnitResponsibilityFields").disable();
		}
	}
}
